/**
 * useCountUp · v2.7.24 (2026-02-21)
 * --------------------------------
 * Smoothly animates a numeric value from its previous render to a new
 * target using `requestAnimationFrame` + an ease-out-cubic curve. Used
 * across the Admin dashboard so every widget refresh feels alive —
 * Trips 69 → 71 ticks up, Cash Today $42 → $58 counts up cent-by-cent.
 *
 * Accepts either a raw number OR a formatted string. Recognizes:
 *   - Plain integers / floats   "69"  · 69
 *   - US currency               "$1,234.56" · "$0.00"
 *   - Percentages               "47%"
 * Non-recognizable strings (e.g. "ONLINE", "—") pass through unchanged.
 */
import { useEffect, useRef, useState } from "react";

const parseValue = (v) => {
  if (typeof v === "number") {
    return {
      num: v,
      // Whole-number formatting if the input is an integer, else 2dp.
      fmt: Number.isInteger(v)
        ? (n) => Math.round(n).toLocaleString()
        : (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    };
  }
  if (v == null) return { num: NaN, fmt: () => "" };
  const s = String(v);
  // Currency e.g. "$1,234.56" or "$42.5" or "$0.00"
  const cur = s.match(/^(\$)([\d,]+(?:\.\d+)?)$/);
  if (cur) {
    const num = parseFloat(cur[2].replace(/,/g, ""));
    if (!Number.isNaN(num)) {
      return {
        num,
        fmt: (n) =>
          `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      };
    }
  }
  // Percent e.g. "47%"
  const pct = s.match(/^(\d+(?:\.\d+)?)(%)$/);
  if (pct) {
    const num = parseFloat(pct[1]);
    if (!Number.isNaN(num)) {
      return { num, fmt: (n) => `${Math.round(n)}%` };
    }
  }
  // Plain integer with optional commas e.g. "1,234" or "69"
  if (/^[\d,]+$/.test(s)) {
    const num = parseInt(s.replace(/,/g, ""), 10);
    if (!Number.isNaN(num)) {
      return { num, fmt: (n) => Math.round(n).toLocaleString() };
    }
  }
  // Plain float "12.5"
  if (/^\d+\.\d+$/.test(s)) {
    const num = parseFloat(s);
    if (!Number.isNaN(num)) {
      const dp = (s.split(".")[1] || "").length;
      return { num, fmt: (n) => n.toFixed(dp) };
    }
  }
  return { num: NaN, fmt: () => s };
};

export default function useCountUp(target, { duration = 600 } = {}) {
  const { num, fmt } = parseValue(target);
  const [current, setCurrent] = useState(num);
  const prevRef = useRef(num);

  useEffect(() => {
    if (Number.isNaN(num)) {
      // Not animatable — bail out, just store and return the raw string.
      prevRef.current = num;
      setCurrent(num);
      return undefined;
    }
    const from = Number.isNaN(prevRef.current) ? num : prevRef.current;
    const to = num;
    if (from === to) {
      setCurrent(to);
      return undefined;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out-cubic — fast start, gentle settle
      const eased = 1 - (1 - t) ** 3;
      setCurrent(from + (to - from) * eased);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevRef.current = to;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [num, duration]);

  if (Number.isNaN(num)) return target;  // pass-through for non-numeric strings
  return fmt(current);
}
